import React from 'react';
import { Link } from 'react-router-dom';
import homeImg from '../assets/howToUse/home.png';
import searchPageImg from '../assets/howToUse/search-page.png';
import copyLinkImg from '../assets/howToUse/copy-link.png';
import imageSearchImg from '../assets/howToUse/image-search.png';
import productPageImg from '../assets/howToUse/product-page.png';
import addToCartModalImg from '../assets/howToUse/add-to-cart-modal.png';
import cartPageImg from '../assets/howToUse/cart-page.png';
import confirmOrderPageImg from '../assets/howToUse/confirm-order-page.png';
import confirmPaymentPageImg from '../assets/howToUse/confirm-payement-page.png';
import paymentSuccessfulImg from '../assets/howToUse/payment-successful-page.png';

const steps = [
  {
    id: 1,
    title: 'Start from Home Page',
    description:
      "Visit our home page to begin your shopping journey. Here you'll find featured products and quick access to our search features.",
    image: homeImg,
    alt: 'Checkstall home page',
  },
  {
    id: 2,
    title: 'Method 1: Search by Keywords',
    description:
      "Use our powerful keyword search to find products. Simply type what you're looking for in the search bar - from electronics to fashion, home goods to industrial supplies.",
    image: searchPageImg,
    alt: 'Product search page with keyword search',
  },
  {
    id: 3,
    title: 'Method 2: Search by 1688 Link',
    description:
      "Found a product on 1688.com? Copy the product link and paste it in our search bar. We'll automatically fetch all product details, specifications, and pricing.",
    image: copyLinkImg,
    alt: 'Copy product link from 1688',
  },
  {
    id: 4,
    title: 'Method 3: Search by Image',
    description:
      "Have a product image but don't know the name? Upload or paste an image URL, and our AI-powered search will find similar products for you.",
    image: imageSearchImg,
    alt: 'Image-based product search',
  },
  {
    id: 5,
    title: 'Browse Product Details',
    description:
      'Click on any product to view detailed specifications, pricing, shipping options, and customer reviews. Compare different variations and suppliers.',
    image: productPageImg,
    alt: 'Detailed product information page',
  },
  {
    id: 6,
    title: 'Add to Cart',
    description:
      'Found what you need? Select your preferred options (size, color, quantity) and add items to your cart. You can continue shopping or proceed to checkout.',
    image: addToCartModalImg,
    alt: 'Add to cart modal with product options',
  },
  {
    id: 7,
    title: 'Review Your Cart',
    description:
      'Review all items in your cart, adjust quantities, and apply any discount codes. Our cart shows real-time pricing in your preferred currency.',
    image: cartPageImg,
    alt: 'Shopping cart page with all items',
  },
  {
    id: 8,
    title: 'Confirm Your Order',
    description:
      'Double-check your order details, shipping address, and payment method. Add any special instructions for your order.',
    image: confirmOrderPageImg,
    alt: 'Order confirmation page',
  },
  {
    id: 9,
    title: 'Complete Payment',
    description:
      'Choose from multiple payment options including bank transfer, card payment, or wallet balance. All payments are secured with industry-standard encryption.',
    image: confirmPaymentPageImg,
    alt: 'Payment confirmation page',
  },
  {
    id: 10,
    title: 'Payment Successful',
    description:
      "Your order is confirmed! You'll receive email updates on your order status, shipping tracking, and delivery notifications.",
    image: paymentSuccessfulImg,
    alt: 'Payment successful confirmation',
  },
];

const youtubeVideoId = 'bWyyLTYTebM';
const youtubeEmbedUrl = `https://www.youtube-nocookie.com/embed/${youtubeVideoId}?rel=0&modestbranding=1`;

const HowToUse = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              How to Use <span className="text-primary">Checkstall</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Follow our simple 10-step guide to find products from China and place your first order.
              From searching to payment, we&apos;ve got you covered.
            </p>
          </div>
        </div>
      </section>

      {/* Video Guide */}
      <section className="pt-12 md:pt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-soft overflow-hidden border border-gray-100">
            <div className="aspect-video bg-gray-900">
              <iframe
                src={youtubeEmbedUrl}
                title="How to Use Checkstall video guide"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {steps.map((step) => (
              <div
                key={step.id}
                className="bg-white rounded-2xl shadow-soft overflow-hidden"
              >
                <div className="p-6 md:p-8">
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    <div className="flex-shrink-0 flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary rounded-2xl flex items-center justify-center">
                        <span className="text-xl font-bold text-white">
                          {step.id}
                        </span>
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                        {step.title}
                      </h2>
                    </div>
                    <div className="md:flex-1">
                      <p className="text-lg text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                  <div className="mt-6 rounded-xl overflow-hidden border border-gray-200">
                    <img
                      src={step.image}
                      alt={step.alt}
                      className="w-full h-auto object-contain bg-gray-50"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="mt-20 text-center bg-white rounded-2xl shadow-soft p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join thousands of Nigerian businesses already importing with Checkstall
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/downloads"
                className="inline-flex justify-center items-center px-8 py-3 rounded-2xl font-semibold bg-primary text-white hover:bg-primary-600 transition-colors duration-200"
              >
                Download Mobile App
              </Link>
              <a
                href="https://wa.me/2348102686096"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex justify-center items-center px-8 py-3 rounded-2xl font-semibold border-2 border-primary text-primary hover:bg-primary hover:text-white transition-colors duration-200"
              >
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowToUse;
